import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Mail, Upload, Globe, Send, Calendar as CalendarIcon, BarChart3,
  LogOut, Plus, Eye, Trash2, RefreshCw, Users, MousePointerClick,
  MailOpen, Clock, CheckCircle2, AlertCircle, Loader2, FileSpreadsheet,
  ChevronRight, ChevronLeft, Sparkles, LayoutDashboard, History, Settings, Menu, X,
  Minus, PlusCircle, EyeOff, ChevronDown, ChevronUp, Image as ImageIcon, Palette, Share2, Copy, Download, Facebook, Instagram,
  HelpCircle, Maximize2, Minimize2, AlertTriangle, MessageSquare
} from "lucide-react";
import { ImagePicker } from "@/components/ImagePicker";
import { SectionImageManager } from "@/components/SectionImageManager";
import { GettingStartedPage, GettingStartedChecklist, TroubleshootingHelp } from "@/components/GettingStartedGuide";
import { useAuth, API, LoadingScreen } from "@/contexts/AuthContext";
import { Layout } from "@/components/layout/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignsList = () => {
  const { authAxios } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all"); // all, draft, scheduled, sent
  const [cleanupStats, setCleanupStats] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // null | 'empty' | 'old'
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const loadAll = useCallback(() => {
    return Promise.all([
      authAxios.get("/campaigns"),
      authAxios.get("/campaigns/scheduled"),
      authAxios.get("/drafts/cleanup-stats"),
    ])
      .then(([allRes, scheduledRes, statsRes]) => {
        setCampaigns(allRes.data);
        setScheduledCampaigns(scheduledRes.data.scheduled_campaigns || []);
        setCleanupStats(statsRes.data);
      })
      .catch(() => toast.error("Failed to load campaigns"))
      .finally(() => setLoading(false));
  }, [authAxios]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runBulkDelete = async (kind) => {
    if (!cleanupStats) return;
    const ids = kind === 'empty'
      ? cleanupStats.empty_drafts.ids
      : cleanupStats.old_drafts.ids;
    if (!ids.length) return;
    setDeleting(true);
    try {
      const res = await authAxios.post("/drafts/bulk-delete", { campaign_ids: ids });
      toast.success(`Deleted ${res.data.deleted} draft${res.data.deleted === 1 ? '' : 's'}`);
      setConfirmAction(null);
      await loadAll();
    } catch {
      toast.error("Couldn't delete those drafts — please try again");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Layout><LoadingScreen /></Layout>;

  const filteredCampaigns = activeFilter === "all" 
    ? campaigns 
    : campaigns.filter(c => c.status === activeFilter);

  const counts = {
    all: campaigns.length,
    draft: campaigns.filter(c => c.status === "draft").length,
    scheduled: campaigns.filter(c => c.status === "scheduled").length,
    sent: campaigns.filter(c => c.status === "sent").length,
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="campaigns-list">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Campaigns</h1>
            <p className="text-slate-500 mt-1">Manage all your email campaigns</p>
          </div>
          <Button onClick={() => navigate("/campaign/new")} className="bg-afa-navy hover:bg-afa-navy-dark rounded-full">
            <Plus className="w-4 h-4 mr-1" /> New Campaign
          </Button>
        </div>

        {/* Scheduled Campaigns Banner */}
        {scheduledCampaigns.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-100 rounded-2xl mb-6">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    {scheduledCampaigns.length} Scheduled Campaign{scheduledCampaigns.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-slate-600">These will be sent automatically at the scheduled time</p>
                </div>
              </div>
              <div className="space-y-2">
                {scheduledCampaigns.slice(0, 3).map((campaign) => (
                  <div 
                    key={campaign.id}
                    onClick={() => navigate(`/campaign/${campaign.id}`)}
                    className="flex items-center justify-between p-3 bg-white/60 rounded-xl cursor-pointer hover:bg-white transition-colors"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{campaign.name}</p>
                      <p className="text-sm text-slate-600">{campaign.recipients} recipients</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-amber-700">
                        {new Date(campaign.scheduled_at).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Draft cleanup banner — only shows if there's clutter to clear */}
        {cleanupStats && (cleanupStats.empty_drafts.count > 0 || cleanupStats.old_drafts.count >= 5) && (
          <Card className="bg-gradient-to-r from-slate-50 to-amber-50/40 border-amber-200/60 rounded-2xl mb-6" data-testid="cleanup-banner">
            <CardContent className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      Clean up your drafts
                    </h3>
                    <p className="text-sm text-slate-600">
                      You have {cleanupStats.total_drafts} draft{cleanupStats.total_drafts === 1 ? '' : 's'} sitting around. Clear out anything you don't need.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {cleanupStats.empty_drafts.count > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setConfirmAction('empty')}
                      data-testid="cleanup-empty-btn"
                      className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 font-medium"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Delete {cleanupStats.empty_drafts.count} empty
                    </Button>
                  )}
                  {cleanupStats.old_drafts.count > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setConfirmAction('old')}
                      data-testid="cleanup-old-btn"
                      className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 font-medium"
                    >
                      <Clock className="w-4 h-4 mr-1.5" />
                      Delete {cleanupStats.old_drafts.count} older than 30 days
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cleanup confirmation dialog */}
        {confirmAction && cleanupStats && (() => {
          const isEmpty = confirmAction === 'empty';
          const bucket = isEmpty ? cleanupStats.empty_drafts : cleanupStats.old_drafts;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !deleting && setConfirmAction(null)} />
              <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="cleanup-confirm-dialog">
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                        Delete {bucket.count} {isEmpty ? 'empty draft' : 'draft'}{bucket.count === 1 ? '' : 's'}?
                      </h3>
                      <p className="text-sm text-slate-600">
                        {isEmpty
                          ? "These drafts have no content — just placeholders. This can't be undone."
                          : "These drafts haven't been touched in over 30 days. This can't be undone."}
                      </p>
                    </div>
                  </div>

                  {bucket.sample && bucket.sample.length > 0 && (
                    <div className="bg-slate-50 rounded-xl p-3 mb-4 max-h-48 overflow-y-auto">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                        Preview {bucket.sample.length < bucket.count ? `(first ${bucket.sample.length} of ${bucket.count})` : ''}
                      </div>
                      <ul className="space-y-1.5 text-sm">
                        {bucket.sample.map(d => (
                          <li key={d.id} className="flex items-center justify-between gap-2 text-slate-700">
                            <span className="truncate">{d.name}</span>
                            {d.created_at && (
                              <span className="text-xs text-slate-400 flex-shrink-0">
                                {new Date(d.created_at).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setConfirmAction(null)}
                      disabled={deleting}
                      className="rounded-full"
                      data-testid="cleanup-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => runBulkDelete(confirmAction)}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold"
                      data-testid="cleanup-confirm-btn"
                    >
                      {deleting ? "Deleting..." : `Delete ${bucket.count}`}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: "all", label: "All" },
            { key: "draft", label: "Drafts" },
            { key: "scheduled", label: "Scheduled" },
            { key: "sent", label: "Sent" },
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeFilter === filter.key 
                  ? "bg-afa-navy text-white" 
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {filter.label} ({counts[filter.key]})
            </button>
          ))}
        </div>

        {filteredCampaigns.length === 0 ? (
          <Card className="bg-white border-slate-100 rounded-2xl">
            <CardContent className="py-16 text-center">
              <Mail className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {activeFilter === "all" ? "No campaigns yet" : `No ${activeFilter} campaigns`}
              </h3>
              <p className="text-slate-500 mb-6">
                {activeFilter === "all" ? "Create your first campaign to get started" : `You don't have any ${activeFilter} campaigns`}
              </p>
              {activeFilter === "all" && (
                <Button onClick={() => navigate("/campaign/new")} className="bg-afa-navy hover:bg-afa-navy-dark rounded-full">
                  Create Campaign
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCampaigns.map((campaign) => (
              <Card 
                key={campaign.id}
                onClick={() => navigate(`/campaign/${campaign.id}`)}
                className="bg-white border-slate-100 rounded-2xl cursor-pointer hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      campaign.status === "scheduled" ? "bg-amber-100" : 
                      campaign.status === "sent" ? "bg-emerald-100" : "bg-afa-navy-light"
                    }`}>
                      {campaign.status === "scheduled" ? (
                        <Clock className="w-6 h-6 text-amber-600" />
                      ) : campaign.status === "sent" ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <Mail className="w-6 h-6 text-afa-navy" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{campaign.name}</h3>
                      <p className="text-sm text-slate-500">
                        {campaign.email_list?.length || 0} recipients • {new Date(campaign.created_at).toLocaleDateString()}
                        {campaign.status === "scheduled" && campaign.scheduled_at && (
                          <span className="text-amber-600 ml-2">
                            → {new Date(campaign.scheduled_at).toLocaleString(undefined, {
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={`status-${campaign.status}`}>{campaign.status}</Badge>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

// Contacts Page - Manage your contact list

export default CampaignsList;
